import { useFormContext } from "react-hook-form";
import { GetCustomsV1Response } from "@/types/api/api-types";
import { CotationUpdateBoFormData } from "@/types/forms/bo/cotationUpdateBoForm.form";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import { AutocompleteSearch } from "@/components/pendingShipments/autocompleteSearch";
import { CountryCode } from "libphonenumber-js";
import { useCallback } from "react";
import clsx from "clsx";
import { UpdateCustomQuotationFormData } from "@/components/backOffice/pendingCustomQuotation/customQuotationQuote.form";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { Button } from "@/components/ui/button";
import { ControlledCustomsSelect } from "@/components/ui/customs/controlled-customs-select";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";
import { useTranslation } from "react-i18next";

interface Props {
  customs: GetCustomsV1Response;
  selectedCustomId: string;
  destinationCountry: string;
  hasCompanyName: boolean;
  forceRenrenderAutocomplete?: number;
}

export const InfoClientsBo = ({
  customs,
  selectedCustomId,
  destinationCountry,
  hasCompanyName,
  forceRenrenderAutocomplete = 0,
}: Props) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<CotationUpdateBoFormData>();
  const countryCode = customs.find((custom) => custom.id === selectedCustomId)?.flagCode || "";
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode || "");

  const handlePlaceClicked = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, countryCode);
      if (!parsed) return;

      methods.setValue("street", parsed.street);
      methods.setValue("postalCode", parsed.postalCode);
      methods.setValue("city", parsed.city);
      if (showStateOrProvince) {
        methods.setValue("stateOrProvinceCode", parsed.stateOrProvinceCode);
      }
      methods.trigger([
        "street",
        "postalCode",
        "city",
        ...(showStateOrProvince ? (["stateOrProvinceCode"] as const) : []),
      ]);
    },
    [methods, showStateOrProvince, countryCode],
  );

  const handleStreetInput = (e: string) => {
    methods.setValue("street", e);
    methods.trigger(["street"]);
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex flex-row items-center gap-2">
        <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
          <p className="text-xl font-bold text-white">1</p>
        </div>
        <p className="text-xl font-bold">{t("infoClient.title")}</p>
      </div>
      <div className="flex w-full flex-col gap-2">
        {hasCompanyName && (
          <ControlledUnifiedInput
            control={methods.control}
            name="company"
            label={t("infoClient.companyLabel")}
            wrapperClassName="w-full"
          />
        )}
        <div className="flex flex-col gap-2 md:flex-row">
          <ControlledUnifiedInput
            control={methods.control}
            name="lastName"
            label={t("infoClient.lastNameLabel")}
            wrapperClassName="w-full"
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="firstName"
            label={t("infoClient.firstNameLabel")}
            wrapperClassName="w-full"
          />
        </div>
        <div className="md:grid-cols-rows2 grid gap-2 md:grid-cols-2 md:grid-rows-1">
          <StyledPhoneInput
            control={methods.control}
            name="phone"
            title={t("infoClient.phoneLabel")}
            defaultCountry={(destinationCountry as CountryCode) || "FR"}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="email"
            label={t("infoClient.emailLabel")}
            wrapperClassName="w-full"
          />
        </div>
        <ControlledCustomsSelect
          disabled
          customs={customs || []}
          control={methods.control}
          name="customId"
          label={t("infoClient.customLabel")}
        />
        <AutocompleteSearch
          key={forceRenrenderAutocomplete}
          placeholder={t("infoClient.streetPlaceholder")}
          countryCode={customs?.find((custom) => custom.id === selectedCustomId)?.countryCode}
          onPlaceClicked={handlePlaceClicked}
          control={methods.control}
          handleStreetInput={handleStreetInput}
        />
        <div className="flex flex-col gap-2 md:flex-row">
          <ControlledUnifiedInput
            control={methods.control}
            name="city"
            label={t("infoClient.cityLabel")}
            wrapperClassName="flex-1"
          />
          {showPostalCode && (
            <ControlledUnifiedInput
              control={methods.control}
              name="postalCode"
              label={t("infoClient.postalCodeLabel")}
              wrapperClassName="flex-1"
            />
          )}
          {showStateOrProvince && (
            <ControlledUnifiedInput
              control={methods.control}
              name="stateOrProvinceCode"
              label={t("infoClient.stateOrProvinceLabel")}
              wrapperClassName="flex-1"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const InfoClientCustomQuotationBo = ({
  customQuotationType,
  customs,
}: {
  customQuotationType: CustomQuotationTypeEnum;
  customs: GetCustomsV1Response;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<UpdateCustomQuotationFormData>();
  const customId = methods.watch("deliveryInfo.customId");

  const countryCode = customs.find((custom) => custom.id === customId)?.flagCode || "";
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode || "");

  const singleDelivery = methods.watch("deliveryInfo.singleDelivery");

  const defaultCountry: CountryCode = (countryCode || "FR") as CountryCode;

  const handlePlaceClicked = (place: google.maps.places.PlaceResult | null) => {
    if (!place) return;
    methods.setValue("deliveryInfo.deliveryStreet", place.name || "");
    methods.setValue(
      "deliveryInfo.deliveryPostalCode",
      place.address_components?.find((el) => el.types.includes("postal_code"))?.long_name || "",
    );
    methods.setValue(
      "deliveryInfo.deliveryCity",
      place.address_components?.find((el) => el.types[0] === "locality")?.long_name || "",
    );
    if (showStateOrProvince) {
      methods.setValue(
        "deliveryInfo.stateOrProvinceCode",
        place.address_components?.find((el) => el.types.includes("administrative_area_level_1"))
          ?.short_name || "",
      );
    }
    methods.trigger([
      "deliveryInfo.deliveryStreet",
      "deliveryInfo.deliveryPostalCode",
      "deliveryInfo.deliveryCity",
      ...(showStateOrProvince ? (["deliveryInfo.stateOrProvinceCode"] as const) : []),
    ]);
  };

  const handleStreetInput = (e: string) => {
    methods.setValue("deliveryInfo.deliveryStreet", e);
    methods.trigger(["deliveryInfo.deliveryStreet"]);
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex w-full flex-col items-start justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">1</p>
          </div>
          <p className="text-xl font-bold">{t("infoClientCustomQuotation.title")}</p>
        </div>
        {customQuotationType === "OTHER" && (
          <div className="flex w-full flex-row justify-center gap-2">
            <Button variant="outline" type="button" onClick={() => {}} disabled={!singleDelivery}>
              {t("infoClientCustomQuotation.singleDelivery")}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={() => {}}
              disabled={singleDelivery}
              className={`rounded-lg border px-4 py-2 transition-colors ${
                !singleDelivery
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {t("infoClientCustomQuotation.multipleDeliveries")}
            </Button>
          </div>
        )}
        <div className={clsx("flex w-full flex-col gap-2", !singleDelivery && "hidden")}>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryLastName"
              label={t("infoClientCustomQuotation.lastNameLabel")}
              wrapperClassName="w-full"
            />
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryFirstName"
              label={t("infoClientCustomQuotation.firstNameLabel")}
              wrapperClassName="w-full"
            />
          </div>
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.deliveryEmail"
            label={t("infoClientCustomQuotation.emailLabel")}
            wrapperClassName="w-full"
          />
          <ControlledUnifiedInput
            control={methods.control}
            name="deliveryInfo.deliveryCompany"
            label={t("infoClientCustomQuotation.companyLabel")}
            wrapperClassName="w-full"
          />
          <ControlledCustomsSelect
            disabled
            customs={customs || []}
            control={methods.control}
            name="deliveryInfo.customId"
            label={t("infoClientCustomQuotation.destinationLabel")}
          />
          <AutocompleteSearch
            placeholder={t("infoClientCustomQuotation.streetPlaceholder")}
            onPlaceClicked={handlePlaceClicked}
            control={methods.control}
            handleStreetInput={handleStreetInput}
            formValueOverride="deliveryInfo.deliveryStreet"
            disabled={!countryCode}
            countryCode={countryCode ? [countryCode] : []}
          />
          <div className="flex flex-col gap-2 sm:flex-row">
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryAdditionalInfo"
              label={t("infoClientCustomQuotation.additionalInfoLabel")}
              disabled={!countryCode}
              wrapperClassName="w-full"
            />
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.deliveryAdditionalInfo2"
              label={t("infoClientCustomQuotation.additionalInfo2Label")}
              disabled={!countryCode}
              wrapperClassName="w-full"
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <ControlledUnifiedInput
              wrapperClassName="flex-1"
              control={methods.control}
              name="deliveryInfo.deliveryCity"
              label={t("infoClientCustomQuotation.cityLabel")}
              disabled={!countryCode}
            />
            {showPostalCode && (
              <ControlledUnifiedInput
                wrapperClassName="flex-1"
                control={methods.control}
                name="deliveryInfo.deliveryPostalCode"
                label={t("infoClientCustomQuotation.postalCodeLabel")}
                disabled={!countryCode}
              />
            )}
            {showStateOrProvince && (
              <ControlledUnifiedInput
                wrapperClassName="flex-1"
                control={methods.control}
                name="deliveryInfo.stateOrProvinceCode"
                label={t("infoClientCustomQuotation.stateOrProvinceLabel")}
                disabled={!countryCode}
              />
            )}
          </div>
          <StyledPhoneInput
            control={methods.control}
            name="deliveryInfo.deliveryPhone"
            title={t("infoClientCustomQuotation.phoneLabel")}
            disabled={!countryCode}
            defaultCountry={defaultCountry}
          />
        </div>
        <ControlledUnifiedInput
          control={methods.control}
          name="deliveryInfo.otherNumberOfExpedition"
          label={t("infoClientCustomQuotation.numberOfDeliveriesLabel")}
          inputType="integer"
          disabled={singleDelivery}
          wrapperClassName={clsx("w-full", singleDelivery && "hidden")}
        />
      </div>
    </div>
  );
};
